import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type { AxiosResponse } from "axios";
import { buildQueryKey } from "@/utils/query";
import type {
  DeviceOrderByParams,
  MoreInfoDataTypes,
} from "@/components/screens/Devices/List/Types/ListDevicesTypes";

export interface IOrganisationDevicesParams {
  search?: string;
  order_by?: DeviceOrderByParams | string;
  page?: number;
  page_size?: number;
}

export interface IOrganisationDeviceStatusChild {
  data: string | number | boolean | null;
  type: MoreInfoDataTypes;
  order: number;
  title: string;
  id: string;
}

export interface IOrganisationDeviceStatus {
  id: ID;
  title: string;
  order: number;
  child_relationship: {
    children: Array<IOrganisationDeviceStatusChild>;
    sections: Array<IOrganisationDeviceStatus>;
  };

  sections: Array<IOrganisationDeviceStatus>;
}

export interface IOrganisationDevice {
  identifier: string;
  id: number;
  created_at: string;
  name: string;
  status: Array<IOrganisationDeviceStatus>;
  accessories: Array<IOrganisationDeviceStatus>;
  device_battery: number;
  device_type: string;
  device_code: string;
  venue_id: number;
  updated_at: string;
  last_seen: string;
  venue: {
    thank_you_message: string;
    id: number;
    tipping_enabled: boolean;
    town: string;
    timezone: string;
    service_min_covers: number;
    discount_on_full_bill: boolean;
    service_charge_on_discount: boolean;
    service_percentage: number;
    created_at: string;
    name: string;
    bill_input_type: number;
    service_mandatory: boolean;
    cashback_percentage: number;
    address1: string;
    configuration_id: number | null;
    updated_at: string;
    postcode: string;
    vat_number: string;
  };
}

export interface IOrganisationDeviceWithDefaultMenu
  extends IOrganisationDevice {
  is_default_menu: boolean;
}

export interface IOrganisationDevicesResponse
  extends APIPaginatedResponse<IOrganisationDevice> {}

export interface IOrganisationDevicesResponseAll {
  pages: IOrganisationDevicesResponse[];
  pageParams: number[];
}

export interface IUpdateDeviceParams {
  name: string;
  identifier: string;
  isVenueSpecific: boolean;
}

export interface IDevicesService {
  list: (
    params: IOrganisationDevicesParams
  ) => UseQueryResult<IOrganisationDevicesResponse, Error>;
  listAll: (
    params: IOrganisationDevicesParams
  ) => UseInfiniteQueryResult<IOrganisationDevicesResponseAll, Error>;
  dataSourceListQuery: (
    params: IOrganisationDevicesParams
  ) => Promise<AxiosResponse<IOrganisationDevicesResponse>>;
  update: (params: {
    venue_id: string;
  }) => UseMutationResult<IOrganisationDevice, Error, IUpdateDeviceParams>;
}

export class DevicesService implements IDevicesService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params: IOrganisationDevicesParams
  ): UseQueryResult<IOrganisationDevicesResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null)
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "devices"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<
          AxiosResponse<IOrganisationDevicesResponse>
        >("/assets/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: updatedParams,
        });

        const results = data.results.map((device) => {
          const deviceInfo = device.status?.find(
            (status) => status.id === "device_information"
          );

          const batteryInfo = deviceInfo?.child_relationship?.sections
            ?.find((section) => section.id === "device_information.battery")
            ?.child_relationship?.children?.find(
              (child) =>
                child.id === "device_information.battery.device_battery"
            )?.data as number;

          const typeInfo = deviceInfo?.child_relationship?.sections
            ?.find((section) => section.id === "device_information.device")
            ?.child_relationship?.children?.find(
              (child) => child.id === "device_information.device.device_type"
            )?.data as string;

          const codeInfo = deviceInfo?.child_relationship?.sections
            ?.find((section) => section.id === "device_information.device")
            ?.child_relationship?.children?.find(
              (child) => child.id === "device_information.device.device_code"
            )?.data as string;

          return {
            ...device,
            accessories:
              typeof device.accessories === "string" ? [] : device.accessories,
            device_battery: batteryInfo ?? null,
            device_type: typeInfo ?? null,
            device_code: codeInfo ?? null,
          };
        });

        return {
          results,
          count: data.count,
          next: data.next,
          previous: data.previous,
        };
      },
    });
  }

  async dataSourceListQuery(
    params: IOrganisationDevicesParams
  ): Promise<AxiosResponse<IOrganisationDevicesResponse>> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null)
    );

    return this.api.get<AxiosResponse<IOrganisationDevicesResponse>>(
      "/assets/",
      {
        endpointService: EndpointService.Gateway,
        params: updatedParams,
      }
    );
  }
  listAll(
    params: IOrganisationDevicesParams
  ): UseInfiniteQueryResult<IOrganisationDevicesResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null)
    );
    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["organisation", "devices", "all"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IOrganisationDevicesResponse>
        >("/assets/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: { ...updatedParams, page: pageParam, page_size: 200 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IOrganisationDevicesResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  update(params: {
    venue_id: string;
  }): UseMutationResult<IOrganisationDevice, Error, IUpdateDeviceParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["organisation", "devices", "update"],
        venueID: params.venue_id,
        params: {},
      }),
      mutationFn: async (params) => {
        const { identifier, isVenueSpecific, ...restParams } = params;
        const { data } = await this.api.patch<
          AxiosResponse<IOrganisationDevice>
        >(`/assets/${identifier}/`, {
          endpointService: EndpointService.Gateway,
          venueSpecific: isVenueSpecific,
          params: restParams,
        });

        return data;
      },
      onMutate: async (newItem) => {},
      onError: (err, newItem, context) => {},
      onSettled: (data, error, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["organisation", "devices"],
        });
      },
    });
  }
}
